import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/* Floating WhatsApp contact button.
   - Only renders on public (non-admin) routes.
   - Fetches /api/settings and respects whatsapp_enabled + whatsapp_link.
   - Appends a pre-fill `?text=` greeting so the chat opens warm.
   - Honours opening hours (London time): outside the window the button dims
     and shows a small "replies by …" bubble to set expectations.
   - Opens in a new tab; wa.me / api.whatsapp.com links hand off to the native
     app on mobile and WhatsApp Web on desktop automatically. */
export default function WhatsAppButton() {
  const [cfg, setCfg] = useState({
    link: "", enabled: false, message: "",
    hoursEnabled: false, open: "", close: "",
  });
  const [now, setNow] = useState(() => Date.now());
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    api.get("/settings")
      .then((r) => {
        if (cancelled) return;
        setCfg({
          link: (r.data?.whatsapp_link || "").trim(),
          enabled: !!r.data?.whatsapp_enabled,
          message: (r.data?.whatsapp_prefill_message || "").trim(),
          hoursEnabled: !!r.data?.whatsapp_hours_enabled,
          open: r.data?.whatsapp_open || "",
          close: r.data?.whatsapp_close || "",
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname]);

  // Re-evaluate open/closed state every minute so the UI flips at the boundary.
  useEffect(() => {
    if (!cfg.hoursEnabled) return;
    const iv = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(iv);
  }, [cfg.hoursEnabled]);

  const open = useMemo(
    () => (cfg.hoursEnabled ? isWithinHours(cfg.open, cfg.close, now) : true),
    [cfg.hoursEnabled, cfg.open, cfg.close, now]
  );

  const onAdmin = pathname.startsWith("/admin");
  if (onAdmin || !cfg.enabled || !cfg.link) return null;

  const base = normaliseLink(cfg.link);
  if (!base) return null;
  const href = cfg.message
    ? `${base}${base.includes("?") ? "&" : "?"}text=${encodeURIComponent(cfg.message)}`
    : base;

  const openLabel = formatHour12(cfg.open);
  const title = open
    ? "Chat with CLAÈ on WhatsApp"
    : openLabel
      ? `We're offline right now — replies by ${openLabel}`
      : "Chat with CLAÈ on WhatsApp";

  return (
    <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[70] flex flex-col items-end gap-2">
      {cfg.hoursEnabled && !open && openLabel && (
        <span
          data-testid="whatsapp-closed-note"
          className="mono-label text-[10px] px-3 py-1.5 bg-stone-900 text-white
                     shadow-[0_6px_18px_rgba(0,0,0,0.18)]
                     rounded-full tracking-wider pointer-events-none
                     animate-[fadeIn_200ms_ease-out]"
          style={{ color: "#fff" }}
        >
          REPLIES BY {openLabel.toUpperCase()}
        </span>
      )}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={title}
        title={title}
        data-testid="whatsapp-float-btn"
        data-whatsapp-open={open ? "true" : "false"}
        className={[
          "flex items-center justify-center",
          "w-14 h-14 rounded-full",
          "bg-[#25D366] text-white",
          "shadow-[0_8px_24px_rgba(37,211,102,0.35)]",
          "ring-1 ring-black/5",
          "transition-all duration-200 ease-out",
          "hover:scale-105 hover:shadow-[0_10px_28px_rgba(37,211,102,0.45)]",
          "active:scale-95",
          "focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2 focus:ring-offset-[#FAF9F6]",
          open ? "" : "opacity-55 saturate-75 cursor-help",
        ].join(" ")}
        style={{ color: "#fff" }}
      >
        <WhatsAppIcon />
        <span className="sr-only">{title}</span>
      </a>
    </div>
  );
}

function WhatsAppIcon() {
  // Font Awesome Free "whatsapp" brand glyph — perfectly centred on its own
  // 448×512 viewBox. Inherits currentColor so the parent button colour wins.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 448 512"
      width="28"
      height="28"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
    </svg>
  );
}

/* Accept the admin's input in any common shape and normalise to an openable URL. */
function normaliseLink(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (/^wa\.me\//i.test(s)) return `https://${s}`;
  if (/^api\.whatsapp\.com\//i.test(s)) return `https://${s}`;
  const digits = s.replace(/[^\d]/g, "");
  if (digits.length < 7) return "";
  return `https://wa.me/${digits}`;
}

/* True iff current London time is inside [open, close) (HH:MM strings). */
function isWithinHours(openStr, closeStr, nowMs) {
  if (!openStr || !closeStr) return true;
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(nowMs));
    const h = Number(parts.find((p) => p.type === "hour")?.value || 0);
    const m = Number(parts.find((p) => p.type === "minute")?.value || 0);
    const nowMin = h * 60 + m;
    const toMin = (s) => {
      const [hh, mm] = s.split(":").map(Number);
      return hh * 60 + mm;
    };
    const o = toMin(openStr);
    const c = toMin(closeStr);
    if (c <= o) return true; // misconfigured — fail open rather than silently hide
    return nowMin >= o && nowMin < c;
  } catch {
    return true;
  }
}

/* "10:00" → "10am", "18:30" → "6:30pm". */
export function formatHour12(hhmm) {
  if (!hhmm) return "";
  const [hRaw, mRaw] = hhmm.split(":").map(Number);
  if (Number.isNaN(hRaw)) return "";
  const h = hRaw;
  const m = Number(mRaw) || 0;
  const period = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, "0")}${period}`;
}
